import { useEffect, useState } from 'react'
import { Button, FormControl, FormErrorMessage, FormHelperText, Input, Select, Text } from '@chakra-ui/react'
import { ClientService, type BillingClientOption } from '@/lib/services/client'
import { MockupFieldLabel } from './FormPanel'

type Props = {
  inheritedClient?: { id: number; name: string }
  selectedClient: BillingClientOption | null
  onChange: (client: BillingClientOption | null) => void
  disabled?: boolean
  disabledReason?: string
  error?: string
}

export function ClientBillingSelection({ inheritedClient, selectedClient, onChange, disabled, disabledReason, error }: Props) {
  const [clients, setClients] = useState<BillingClientOption[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [refresh, setRefresh] = useState(0)
  useEffect(() => {
    if (inheritedClient?.id || disabled) return
    let cancelled = false
    setLoading(true)
    setLoadError(false)
    ClientService.getClientOptions()
      .then((rows) => { if (!cancelled) setClients(rows) })
      .catch(() => { if (!cancelled) setLoadError(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [inheritedClient?.id, disabled, refresh])

  return (
    <FormControl isInvalid={Boolean(error)} mb={4}>
      <MockupFieldLabel>Client</MockupFieldLabel>
      {inheritedClient ? <>
        <Input aria-label="Client" size="sm" value={inheritedClient.name} isReadOnly />
        <FormHelperText>Client inherited from ticket request.</FormHelperText>
      </> : <>
        <Text fontSize="sm" mb={2}>{disabledReason || 'This request has no assigned client.'}</Text>
        <Select aria-label="Select client" size="sm" value={selectedClient?.id ?? ''}
          placeholder={loading ? 'Loading clients…' : 'Select client'} isDisabled={disabled || loading || loadError}
          onChange={(event) => onChange(clients.find((client) => client.id === Number(event.target.value)) ?? null)}>
          {clients.map((client) => <option key={client.id} value={client.id}>{client.name} ({client.code})</option>)}
        </Select>
        {!loading && !loadError && !disabled && clients.length === 0 && <Text fontSize="xs" mt={2}>No clients available.</Text>}
        <FormHelperText>Select a client to configure billing and field engineer dispatch. Saving assigns this client to both the request and parent ticket. Changing the selection clears the purchase order and dispatch choices.</FormHelperText>
        {loadError && <Text role="alert" color="red.500" fontSize="sm">Unable to load clients.</Text>}
        {!disabled && <>
          <Text fontSize="xs" mt={2}>Client not listed? Ask an authorized administrator to register it in client management, then refresh this list.</Text>
          <Button size="xs" variant="link" mt={2} isDisabled={loading} onClick={() => setRefresh((value) => value + 1)}>
            {loadError ? 'Retry loading clients' : 'Refresh clients'}
          </Button>
        </>}
      </>}
      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  )
}
